import { css } from '@emotion/react'
import { FutureMessageTemplateCard } from 'components/futureMessage/futureMessageTemplateModal/FutureMessageTemplateCard'
import { SCardsWrapper, SDescription, STitleRow } from './FutureMessageTemplateModal.styled'
import {
  createFutureMessageWithRedirect,
  CreateFutureMessageWithRedirectParams,
  FutureMessageTemplate,
  GET_FUTURE_MESSAGE_TEMPLATES,
} from 'components/futureMessage/FutureMessageUtils'
import { Icon } from 'components/icon'
import { useModal } from 'components/modal/Modal'
import { BlockSpinner } from 'components/spinner/Spinner'
import { Title } from 'components/Typography'
import { useRef } from 'react'
import { fireEvent } from 'utils/analytics'
import { useDynamicTranslations } from 'utils/i18n/useDynamicTranslations'
import { BREAKPOINTS, pxToRem } from 'utils/styled'
import { useTranslation } from 'next-i18next'

const FutureMessageTemplateModal = (props: {
  onChooseTemplate: (template: FutureMessageTemplate) => void
  close: () => void
}) => {
  const { t, isLoading } = useDynamicTranslations('life_stories')
  const { t: tCommon } = useTranslation()
  if (isLoading) return <BlockSpinner />
  return (
    <>
      <STitleRow>
        <Icon name="File" />
        <Title variant="h7">{t('life_stories.inside_page.future_messages.modal_title')}</Title>
      </STitleRow>
      <SDescription>{t('life_stories.inside_page.future_messages.modal_desc')}</SDescription>
      <SCardsWrapper>
        {Object.values(GET_FUTURE_MESSAGE_TEMPLATES(tCommon)).map((template) => (
          <FutureMessageTemplateCard
            key={template.type}
            onClick={() => {
              fireEvent('fm_template_selected', { template: template.type })

              props.onChooseTemplate(template)
              props.close()
            }}
            template={template}
          />
        ))}
      </SCardsWrapper>
    </>
  )
}

export const useFutureMessageTemplateModal = (
  onChooseTemplate: (template: FutureMessageTemplate) => void
) => {
  const [shareModal, openShareModal, closeShareModal] = useModal(
    ({ isOpen }) =>
      isOpen ? (
        <FutureMessageTemplateModal close={closeShareModal} onChooseTemplate={onChooseTemplate} />
      ) : null,
    {
      modalCss: css`
        @media ${BREAKPOINTS.MD.min} {
          max-width: ${pxToRem(720)}rem;
        }
      `,
      onClose: () => {
        fireEvent('fm_template_selected', { template: 'closed' })
      },
    }
  )
  return [shareModal, openShareModal, closeShareModal] as const
}

// this extends openModal method with custom params which is used in onChooseTemplate action
export const useFutureMessageTemplateModalWithRedirectParams = () => {
  const paramsRef = useRef<CreateFutureMessageWithRedirectParams | undefined>()

  const onChooseTemplate = (template: FutureMessageTemplate) => {
    if (paramsRef.current) {
      createFutureMessageWithRedirect({ ...paramsRef.current, templateType: template.type })
    }
  }

  const [shareModal, _openShareModal, closeShareModal] =
    useFutureMessageTemplateModal(onChooseTemplate)

  const openShareModal = (params: CreateFutureMessageWithRedirectParams) => {
    paramsRef.current = params
    _openShareModal()
  }

  return [shareModal, openShareModal, closeShareModal] as const
}
